import cn from 'classnames';
import { type ReactNode, useId, useRef } from 'react';
import { createPortal } from 'react-dom';

import { useBreakpoints } from '../../../hooks/useBreakpoints';
import { useEscapeToClose } from '../../../hooks/useEscapeToClose';
import { useFocusTrap } from '../../../hooks/useFocusTrap';
import { usePreventScroll } from '../../../hooks/usePreventScroll';

import { TriggerButton, type TriggerProps } from '../TriggerButton';
import { FrontPageModalFooter } from './components/frontPageModalFooter';
import { FrontPageModalHeader } from './components/frontPageModalHeader';

import styles from './FrontPageModal.module.css';
import { useControllableState } from './hooks/useControllableState';
import { useInertSiblings } from './hooks/useInertSiblings';

export interface FrontPageModal {
    trigger: {
        text: TriggerProps['text'];
        prefix?: TriggerProps['prefix'];
        aria?: {
            label?: string;
        };
    };
    breakpoint?: 'sm' | 'md' | 'lg';
    children?: ReactNode;
    title: string;
    isModalOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
    hideChevron?: boolean;
    error?: string | null;
    showFooter?: boolean;
}

export const FrontPageModal = ({
    breakpoint,
    children,
    trigger,
    title,
    isModalOpen,
    onOpenChange,
    hideChevron,
    error,
    showFooter = true,
}: FrontPageModal) => {
    const [open, setOpen] = useControllableState<boolean>({
        value: isModalOpen,
        defaultValue: false,
        onChange: onOpenChange,
    });

    const dialogId = `fp-modal-${useId()}`;
    const titleId = `${dialogId}-title`;
    const triggerRef = useRef<HTMLButtonElement>(null);

    const closeModal = () => {
        setOpen(false);
        queueMicrotask(() => triggerRef.current?.focus());
    };

    const toggleOpen = () => setOpen((prev) => !prev);

    const modalRef = useRef<HTMLDivElement>(null);
    const matchesBreakpoint = useBreakpoints({ breakpoint: breakpoint ?? 'sm' });

    useFocusTrap(open, modalRef);
    useEscapeToClose(open, closeModal);
    usePreventScroll(open && matchesBreakpoint);
    useInertSiblings({ containerRef: modalRef, active: open && matchesBreakpoint });

    const modal = (
        <div
            ref={modalRef}
            tabIndex={-1}
            aria-hidden={!open}
            data-fp-modal-root
            className={cn(styles.modal, {
                [styles['modal--open']]: open,
                [styles['modal--close']]: !open,
            })}
            {...(open && {
                role: 'dialog',
                ...(matchesBreakpoint ? { 'aria-modal': true } : {}),
                'aria-labelledby': titleId,
                id: dialogId,
            })}>
            {/* These are out of order to support focus order in Safari, CSS is used to organise them visually */}
            <div className={styles.modal__content}>{children}</div>
            <FrontPageModalHeader title={title} onClose={closeModal} />
            {showFooter && <FrontPageModalFooter onClose={closeModal} />}
        </div>
    );

    return (
        <div className={cn(styles.wrapper, breakpoint && styles[`wrapper--${breakpoint}`])}>
            <TriggerButton
                ref={triggerRef}
                onTrigger={toggleOpen}
                text={trigger.text}
                prefix={trigger.prefix}
                hideChevron={hideChevron}
                error={error}
                open={open}
                aria={{
                    label: trigger.aria?.label,
                    hasPopup: 'dialog',
                    controls: open ? dialogId : undefined,
                }}
            />
            {open && (matchesBreakpoint ? createPortal(modal, document.body) : modal)}
        </div>
    );
};
